import type { CascadeOption, CascadeValue } from '@byecode/ui'
import { CascadeList, IconFont, Popover, usePopoverHeight } from '@byecode/ui'
import type { CascadeConfig } from '@lighthouse/core'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncRetry, useToggle, useUpdateEffect } from 'react-use'

import { useAppThemeContext } from '../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import { popoverMaxHeight } from '../../constant'
import { SelectPlaceHolder } from '../../SelectPlaceHolder'
import type { FieldBaseProps } from '../../types'
import * as SC from './styles'

interface DepartmentFieldProps extends FieldBaseProps {
    value: string[]
    cascadeConfig: CascadeConfig
}

const DepartmentField: React.FunctionComponent<DepartmentFieldProps> = props => {
    const {
        value: data,
        isControlled,
        config: { placeholder, breakPoint },
        readOnly,
        record,
        cascadeConfig,
        dataSource,
        onChange,
        onFetchCascadeOptions
    } = props
    const { cascade, fieldPointer = '' } = cascadeConfig ?? {}
    const {
        cascadeFieldPointer = '',
        cascadePointer = '=',
        showPath,
        isLastLevel,
        filter,
        parentFieldPointer = '',
        sortFieldPointer = '',
        cascadeShowFieldPointer = ''
    } = cascade ?? {}
    const { theme } = useAppThemeContext()
    const fieldType = dataSource?.schema?.[fieldPointer]?.type
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const isDepartment = fieldType === 'department'
    const isParentDepartment = fieldType === 'parentDepartment'

    const [value, setValue] = useState(data)
    const [opened, setOpened] = useToggle(false)

    const { value: options, retry } = useAsyncRetry(async () => {
        const data = await onFetchCascadeOptions?.({
            fieldPointer: 'ID',
            showFieldPointer: 'DEPARTMENT_NAME',
            dsId: 'DEPARTMENT_DATASOURCE',
            sortFieldPointer,
            filter,
            disableId: isParentDepartment ? record?.content?.['ID']?.value?.toString() : '',
            parentFieldPointer: 'PARENT_DEPARTMENT'
        })
        return data ?? []
    }, [
        cascadeFieldPointer,
        cascadePointer,
        sortFieldPointer,
        filter,
        parentFieldPointer,
        cascadeShowFieldPointer,
        isParentDepartment,
        onFetchCascadeOptions
    ])

    const { t } = useTranslation()

    useUpdateEffect(() => {
        setValue(data)
    }, [data])

    const { ref, height: maxHeight } = usePopoverHeight({ opened, popoverMaxHeight })

    const flatOptions = useMemo(() => {
        function getChildOption(option: CascadeOption, list: CascadeOption[]) {
            const { value, label, path, labelPath, children = [] } = option
            let arrList = list
            for (const child of children) {
                const newChild = {
                    ...child,
                    path: `${path}/${child.value}`,
                    labelPath: `${labelPath}/${child.label}`,
                    isLast: (child.children ?? []).length === 0
                }
                arrList = [...arrList, newChild, ...getChildOption(newChild, [])]
            }
            return arrList
        }
        return (
            options?.reduce<CascadeOption[]>((pre, cur) => {
                const newOption = { ...cur, path: cur.value, labelPath: cur.label, isLast: (cur.children ?? []).length === 0 }
                return [...pre, newOption, ...getChildOption(newOption, [])]
            }, []) ?? []
        )
    }, [options])

    const tagList = useMemo(() => value.map(v => flatOptions.find(option => option.value === v)), [flatOptions, value])

    const handleChange = useCallback(
        (v: CascadeValue) => {
            setValue(v)
            onChange?.({ type: 'cascade', value: v })
            !isDepartment && setOpened(false)
        },
        [isDepartment, onChange, setOpened]
    )

    const handleClear = useCallback(
        (id?: string) => {
            const newValue = id ? value.filter(v => v !== id) : []
            setValue(newValue)
            onChange?.({ type: 'cascade', value: newValue })
        },
        [onChange, value]
    )

    return (
        <Popover opened={opened} zIndex={200} position="bottom-start" onChange={setOpened} withinPortal width="target" disabled={readOnly}>
            <Popover.Target>
                <SC.Container
                    type="button"
                    data-field-input
                    data-field-active={opened}
                    data-field-disabled={!!readOnly}
                    onClick={() => {
                        setOpened(true)
                    }}
                >
                    <SC.Wrapper ref={ref}>
                        <SC.TagWrapper>
                            {tagList.map(v => (
                                <SC.Tag key={v?.value}>
                                    <SC.TagLabel>{showPath ? v?.labelPath : v?.label}</SC.TagLabel>
                                    <IconFont
                                        data-field-icon
                                        type="Close"
                                        size={16}
                                        onClick={e => {
                                            e.stopPropagation()
                                            v?.value && handleClear(v?.value)
                                        }}
                                    />
                                </SC.Tag>
                            ))}
                            {tagList.length === 0 && <SelectPlaceHolder color={placeholderColor} label={placeholder} />}
                        </SC.TagWrapper>
                    </SC.Wrapper>
                    {!readOnly && (
                        <SC.IconWrapper data-field-icon>
                            {tagList.length > 0 && (
                                <IconFont
                                    type="CloseCircle"
                                    size={16}
                                    onClick={e => {
                                        e.stopPropagation()
                                        handleClear()
                                    }}
                                />
                            )}
                            <IconFont type="ArrowDownSmall" size={16} />
                        </SC.IconWrapper>
                    )}
                </SC.Container>
            </Popover.Target>
            <Popover.Dropdown compact style={{ fontFamily }}>
                <CascadeList
                    data={value}
                    onChange={handleChange}
                    // onPathChange={onPathChange}
                    selectionMode={isLastLevel ? 'leaf-only' : 'any'}
                    searchable
                    emptyProps={{
                        description: t('noFindData')
                    }}
                    multiple={isDepartment}
                    searchPlaceholder={t('search')}
                    options={options}
                    style={{ maxHeight }}
                    styles={{
                        columns: {
                            minHeight: 200
                        },
                        search: {
                            '&:focus-within': {
                                borderColor: 'var(--color-app-main)'
                            }
                        }
                    }}
                />
            </Popover.Dropdown>
        </Popover>
    )
}

export default DepartmentField
